import { Button, Tooltip } from "antd";
import { supportAccessCopy } from "app/supportAccessCopy";
import { useSupportAccessSession } from "hooks/useSupportAccessSession";
import { useEffect, useRef, useState } from "react";
import { clearActAsGrantId, getActAsGrantId } from "utils/supportAccess";

/**
 * The staff-facing session bar.
 *
 * NAMED `SupportAccessBar`, not `ImpersonationBanner`. The name leaks into
 * aria-labels, test ids and eventually a customer-facing string; "impersonation"
 * describes the mechanism and is the wrong word to have escape.
 *
 * 🔑 IT RENDERS FROM THE SERVER'S ANSWER, not from what the client remembered.
 * 🔑 ITS ABSENCE IS NOT A SIGNAL. An earlier draft of this design argued the
 * bar's absence would tell a staff member they were out of a session, while a
 * different part of the same design argued the bar becomes wallpaper within a
 * week. Both cannot hold, and a thing nobody notices cannot be noticed to be
 * missing. So a mismatch renders as PRESENCE: when this tab is sending an
 * act-as header and the server says there is no session, that is a loud alarm
 * rather than a quiet nothing.
 */
export function SupportAccessBar() {
	const { session, isUnknown } = useSupportAccessSession();
	const clientGrantId = getActAsGrantId();

	const active = session?.active === true;
	// The client believes it is in a session and the server disagrees. Never a
	// silent no-op: the browser is showing a customer's data under a staff
	// member's own identity, or the reverse.
	const mismatch = Boolean(clientGrantId) && session?.active === false;
	const unconfirmed = Boolean(clientGrantId) && isUnknown && !session;

	if (!active && !mismatch && !unconfirmed) return null;

	if (mismatch || unconfirmed) {
		return (
			<div style={alarmStyle} role="alert" data-testid="support-access-alarm">
				<strong>
					{mismatch
						? "This tab thinks it is in a support session. The server says it is not."
						: "Cannot confirm whether this is a support session."}
				</strong>
				<span style={{ opacity: 0.9 }}>
					{mismatch
						? "Anything you do here is happening as YOU, not as the customer. Stop and end the session."
						: "Do not act until this resolves."}
				</span>
				<Button
					size="small"
					danger
					onClick={() => {
						clearActAsGrantId();
						window.location.reload();
					}}
				>
					Clear session
				</Button>
			</div>
		);
	}

	return <ActiveBar />;
}

function ActiveBar() {
	const { session, refresh } = useSupportAccessSession();
	const remaining = useCountdown(session?.expiresAt);
	const ref = usePublishedHeight();

	// `=== "write"`, never `!== "read"`. An API pod that predates the mode field
	// omits it, and a grant row written before the column existed reads back
	// undefined — both must render as the read-only bar the operator expects,
	// not as an alarm nobody can explain.
	const canWrite = session?.mode === "write";

	return (
		<div
			ref={ref}
			style={canWrite ? writeBarStyle : barStyle}
			role="status"
			data-testid="support-access-bar"
			data-mode={canWrite ? "write" : "read"}
		>
			<span aria-hidden>{canWrite ? "✍️" : "👁"}</span>
			<strong>
				{canWrite
					? supportAccessCopy.staffBar.actingWrite
					: supportAccessCopy.staffBar.acting}
			</strong>
			<span style={{ opacity: 0.9 }} data-testid="support-access-subject">
				{/*
				 * 🪤 An account-wide session has NO companyId. The naive render puts
				 * "Company #undefined" on the one banner whose entire job is to say
				 * what you are inside — and it reads as a bug, so the operator stops
				 * trusting the bar that is the safety mechanism.
				 */}
				User #{session?.subjectUserId} ·{" "}
				{session?.companyId == null
					? "whole account"
					: `Company #${session.companyId}`}
			</span>
			<Tooltip title="Ends automatically at the time the customer approved.">
				<span data-testid="support-access-countdown">{remaining}</span>
			</Tooltip>
			<Button
				size="small"
				onClick={async () => {
					if (session?.grantId) {
						try {
							const { api } = await import("utils/axios");
							await api.post(`support-access/grants/${session.grantId}/end`);
						} catch {
							// Ending is best-effort from the client's side; clearing the
							// local id below stops this tab acting regardless, and the
							// grant still expires server-side on its own.
						}
					}
					clearActAsGrantId();
					await refresh();
					window.location.reload();
				}}
			>
				End session
			</Button>
		</div>
	);
}

/**
 * Publishes this element's height as `--support-access-bar-height` so the two
 * 100vh panes below can subtract it.
 *
 * MEASURED rather than hardcoded because the bar wraps on narrow viewports —
 * a constant would be right at desktop width and wrong exactly where the layout
 * is already tightest.
 */
function usePublishedHeight() {
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const root = document.documentElement;
		const publish = () =>
			root.style.setProperty(
				"--support-access-bar-height",
				`${el.offsetHeight}px`,
			);
		publish();

		const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver })
			.ResizeObserver;
		const observer = RO ? new RO(publish) : null;
		observer?.observe(el);

		return () => {
			observer?.disconnect();
			// Must be removed, not set to 0: leaving a stale height behind would
			// shrink both panes by a bar that is no longer on screen.
			root.style.removeProperty("--support-access-bar-height");
		};
	}, []);

	return ref;
}

/**
 * Counts down from the SERVER's expiry, re-read on every echo.
 *
 * Clamped at zero: a negative countdown reads as a very long remaining time at a
 * glance, which is the opposite of the truth.
 */
export function useCountdown(expiresAt?: string): string {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, []);

	if (!expiresAt) return "";
	const ms = new Date(expiresAt).getTime() - now;
	if (Number.isNaN(ms)) return "";
	if (ms <= 0) return "Expired";

	const total = Math.floor(ms / 1000);
	const mm = Math.floor(total / 60);
	const ss = total % 60;
	return `${mm}:${String(ss).padStart(2, "0")} left`;
}

const base: React.CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 12,
	padding: "8px 16px",
	fontSize: 13,
	// ABOVE AntD's Modal and Drawer (1000) and its notifications (1010).
	// At the header's own z-index of 10 this bar sat UNDER a modal mask — dimmed
	// at exactly the moment a confirmation dialog was on screen, which is the
	// moment it most needs to be readable.
	zIndex: 1100,
	position: "sticky",
	top: 0,
	// Wrap rather than overflow on narrow viewports: the sider is a fixed
	// 265/80px (SIDER_WIDTH), so there is no width at which hiding part of this is acceptable.
	flexWrap: "wrap",
};

const barStyle: React.CSSProperties = {
	...base,
	background: "#7c3aed",
	color: "white",
};

/**
 * Write mode gets its OWN colour, not a badge on the familiar one.
 *
 * The purple bar's job is reassurance, and after a week it is wallpaper — read
 * without being read. A word added inside wallpaper is not seen. The state that
 * inverts the bar's meaning has to invert its appearance, so the operator
 * registers it peripherally before they read anything.
 *
 * Deliberately NOT the alarm red of `alarmStyle`: that colour already means
 * "this tab is lying to you, stop", and reusing it would make a working feature
 * indistinguishable from a broken one.
 */
const writeBarStyle: React.CSSProperties = {
	...base,
	background: "#c2410c",
	color: "white",
};

const alarmStyle: React.CSSProperties = {
	...base,
	background: "#b91c1c",
	color: "white",
};
